import React from "react";
import { PROJECT_BLOCKS, ROW_SHAPES, AUTO_MANUAL_RULES, DECISION_TREE } from "./catalog";
import {
  blockMonoLabelClass,
  blockTechnicalLabelClass,
  blockBodyClass,
  blockMetaLineClass,
  figureRatioClass,
} from "./layoutTokens";

export function BlockText({ heading, paragraphs = [], className = "" }) {
  return (
    <div className={className}>
      {heading && <p className={`${blockMonoLabelClass} mb-2`}>{heading}</p>}
      {paragraphs.map((p, i) => (
        <p key={i} className={`${blockBodyClass} mb-3 last:mb-0`}>
          {p}
        </p>
      ))}
    </div>
  );
}

export function BlockQuote({ quote, attribution, className = "" }) {
  return (
    <blockquote
      className={`${blockBodyClass} max-w-[74ch] leading-snug font-normal ${className}`}
    >
      <span className="text-content-secondary dark:text-neutral-400">&ldquo;</span>
      {quote}
      <span className="text-content-secondary dark:text-neutral-400">&rdquo;</span>
      {attribution && (
        <footer className="mt-2 font-sans normal-case tracking-normal text-meta text-content-muted dark:text-neutral-500 font-light">
          — {attribution}
        </footer>
      )}
    </blockquote>
  );
}

export function BlockSpec({ lines = [], className = "" }) {
  return (
    <div className={`${blockMetaLineClass} leading-relaxed max-w-[74ch] ${className}`}>
      {lines.join(" · ")}
    </div>
  );
}

export function BlockPAOCell({ role, body, className = "" }) {
  return (
    <div className={className}>
      <p className={`${blockTechnicalLabelClass} mb-1`}>{role}</p>
      <p className="text-body leading-snug max-w-none text-black dark:text-neutral-100">{body}</p>
    </div>
  );
}

export function BlockStepsRow({ steps = [] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
      {steps.map((s, i) => (
        <div key={i}>
          <p className={`${blockTechnicalLabelClass} mb-1`}>
            Steps · {String(i + 1).padStart(2, "0")}
          </p>
          <p className="text-body leading-snug max-w-[26ch] text-black dark:text-neutral-100">{s}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Real image figure, pinned to one of the four canonical aspect ratios so
 * every cell in a row lines up. Use this instead of a raw <img> on case-study
 * pages.
 *
 * Props:
 *   src       — image URL
 *   alt       — alt text (required)
 *   ratio     — "banner" (3:1) | "video" (16:9) | "classic" (4:3, default) | "square" (1:1)
 *   fit       — "cover" (default) | "contain"
 *   invert    — bool; apply dark:invert for diagrams/tables on dark backgrounds
 *   caption   — optional small caption shown below the image
 *   className — extra classes on the outer figure
 */
export function BlockFigure({
  src,
  alt,
  ratio = "classic",
  fit = "cover",
  invert = false,
  caption,
  className = "",
}) {
  const ratioCls = figureRatioClass(ratio);
  const fitCls = fit === "contain" ? "object-contain" : "object-cover";
  const bgCls = fit === "contain" ? "bg-neutral-50 dark:bg-neutral-950" : "bg-neutral-100 dark:bg-neutral-900";
  const invertCls = invert ? "dark:invert" : "";
  return (
    <figure className={`flex flex-col w-full h-full ${className}`}>
      <div className={`relative w-full ${ratioCls} overflow-hidden ${bgCls}`}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${fitCls} ${invertCls}`}
        />
      </div>
      {caption ? (
        <figcaption className={`${blockTechnicalLabelClass} px-3 py-2 border-t border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950`}>
          <span aria-hidden className="mr-1.5 text-content-subtle dark:text-neutral-600">▸</span>
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

/** Placeholder media cell — aspect ratio via Tailwind arbitrary value */
export function BlockMediaPlaceholder({
  label,
  variant = "light",
  spanLabel,
  aspectClass = "aspect-video",
  className = "",
}) {
  const bg =
    variant === "dark"
      ? "bg-neutral-300 dark:bg-neutral-700"
      : variant === "darker"
      ? "bg-neutral-400 dark:bg-neutral-600"
      : "bg-neutral-200 dark:bg-neutral-800";

  return (
    <div
      className={`relative border border-border dark:border-neutral-700 flex flex-col items-center justify-center ${bg} ${aspectClass} ${className}`}
      style={{
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(10,10,10,.06) 0 1px, transparent 1px 8px)",
      }}
    >
      {spanLabel && (
        <span className="absolute right-2 top-2 font-mono text-meta tracking-[0.04em] px-1 py-0.5 border border-content-subtle dark:border-neutral-500 bg-white/90 dark:bg-black/50">
          {spanLabel}
        </span>
      )}
      <span className="font-mono text-meta uppercase text-content-secondary dark:text-neutral-300 text-center px-4">
        {label}
      </span>
    </div>
  );
}

export function BlockDiagramPlaceholder({ className = "" }) {
  return (
    <div
      className={`border border-border dark:border-neutral-700 bg-white dark:bg-black ${className}`}
    >
      <svg viewBox="0 0 320 120" className="w-full h-auto text-black dark:text-white" aria-hidden>
        <rect x="24" y="36" width="72" height="48" fill="none" stroke="currentColor" strokeWidth="1" />
        <text x="60" y="66" textAnchor="middle" className="fill-current font-mono text-[9px]">
          NODE
        </text>
        <line x1="96" y1="60" x2="140" y2="60" stroke="currentColor" strokeWidth="1" />
        <polygon points="136,56 144,60 136,64" fill="currentColor" />
        <rect x="144" y="30" width="100" height="60" fill="none" stroke="currentColor" strokeWidth="1" />
        <text x="194" y="68" textAnchor="middle" className="fill-current font-mono text-[9px]">
          DIAGRAM
        </text>
      </svg>
    </div>
  );
}

export function BlockDecision({ label, children }) {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-[110px_1fr] gap-5 md:gap-6 items-start border border-black dark:border-white bg-surface-muted dark:bg-neutral-950 px-5 py-4 md:px-6 md:py-5">
      <span className={`${blockMonoLabelClass} text-black dark:text-white font-medium`}>
        {label}
      </span>
      <div className={`${blockBodyClass} text-black dark:text-neutral-200 max-w-none space-y-2`}>{children}</div>
    </div>
  );
}

export function BlockDiff({ leftTitle, rightTitle, leftItems = [], rightItems = [] }) {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 border border-border dark:border-neutral-800">
      <div className="border-b md:border-b-0 md:border-r border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950 p-4 md:p-5">
        <h4
          className={`flex justify-between ${blockTechnicalLabelClass} mb-3 items-baseline gap-2 text-content-secondary dark:text-neutral-400`}
        >
          <span className="text-black dark:text-white font-medium">{leftTitle}</span>
          <span className="text-content-muted">{leftItems.length}</span>
        </h4>
        <ul className="space-y-0">
          {leftItems.map((item, i) => (
            <li
              key={i}
              className="grid grid-cols-[22px_1fr] gap-2 pt-2 border-t border-border dark:border-neutral-800 first:border-t-0 first:pt-0 text-body"
            >
              <span className="font-mono text-meta text-content-muted">{item.num}</span>
              <div>
                <span className="font-medium text-black dark:text-white">{item.title}</span>
                {item.note && (
                  <span className="block font-sans normal-case italic text-meta text-content-secondary dark:text-neutral-400 mt-0.5">
                    {item.note}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="p-4 md:p-5">
        <h4
          className={`flex justify-between ${blockTechnicalLabelClass} mb-3 items-baseline gap-2 text-content-secondary dark:text-neutral-400`}
        >
          <span className="text-black dark:text-white font-medium">{rightTitle}</span>
          <span className="text-content-muted">{rightItems.length}</span>
        </h4>
        <ul className="space-y-0">
          {rightItems.map((item, i) => (
            <li
              key={i}
              className="grid grid-cols-[22px_1fr] gap-2 pt-2 border-t border-border dark:border-neutral-800 first:border-t-0 first:pt-0 text-body"
            >
              <span className="font-mono text-meta text-content-muted">{item.num}</span>
              <div>
                <span className="font-medium">{item.title}</span>
                {item.note && (
                  <span className="block font-sans normal-case italic text-meta text-content-secondary dark:text-neutral-400 mt-0.5">
                    {item.note}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AllowedSpansLegend() {
  return (
    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
      {ROW_SHAPES.map((row) => (
        <div
          key={row.id}
          className="border border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950 px-3 py-3 text-center"
        >
          <span className="font-mono text-meta uppercase text-black dark:text-white block mb-1.5">
            {row.formula.split("=")[0].trim()}
          </span>
          <span className="text-lede font-medium tracking-tight text-black dark:text-white block mb-1">
            {row.label}
          </span>
          <span className="text-body text-content-secondary dark:text-neutral-500 leading-snug">
            {row.example}
          </span>
        </div>
      ))}
    </div>
  );
}

export function RulesGrid() {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
      {AUTO_MANUAL_RULES.map((r) => (
        <div
          key={r.id}
          className="border border-border dark:border-neutral-800 bg-surface-muted dark:bg-neutral-950 p-4 flex flex-col gap-1.5"
        >
          <span className={`${blockTechnicalLabelClass} tracking-[0.08em]`}>Rule {r.id}</span>
          <span className="font-medium text-body text-black dark:text-white">{r.title}</span>
          <p className="text-body text-content-secondary dark:text-neutral-400 m-0 leading-relaxed">
            {r.body}
          </p>
        </div>
      ))}
    </div>
  );
}

export function DecisionTreeBlock() {
  return (
    <div className="mt-6 border border-border dark:border-neutral-800 bg-white dark:bg-black p-5 md:p-6">
      {DECISION_TREE.map((line, i) => (
        <div
          key={i}
          className="grid grid-cols-[28px_1fr] gap-3 py-3 items-baseline text-body leading-relaxed first:pt-0"
        >
          <span className="font-mono text-meta text-content-muted">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-content-secondary dark:text-neutral-300">{line}</span>
        </div>
      ))}
    </div>
  );
}

export function BlocksCatalogTable() {
  return (
    <div className="mt-4 overflow-x-auto border border-border dark:border-neutral-800 rounded-none">
      <table className="w-full text-left text-body">
        <thead className={`${blockTechnicalLabelClass} bg-surface-muted dark:bg-neutral-950 border-b border-border dark:border-neutral-800`}>
          <tr>
            <th className="px-3 py-2 font-medium">ID</th>
            <th className="px-3 py-2 font-medium">Block</th>
            <th className="px-3 py-2 font-medium">Typical spans</th>
            <th className="px-3 py-2 font-medium hidden lg:table-cell">Notes</th>
          </tr>
        </thead>
        <tbody>
          {PROJECT_BLOCKS.map((b) => (
            <tr key={b.id} className="border-t border-border dark:border-neutral-800">
              <td className="px-3 py-2 font-mono text-meta text-content-muted">{b.id}</td>
              <td className="px-3 py-2 font-medium text-black dark:text-white">{b.name}</td>
              <td className="px-3 py-2 text-content-secondary dark:text-neutral-400">
                {b.spans.join(", ")}
              </td>
              <td className="px-3 py-2 text-content-secondary dark:text-neutral-400 hidden lg:table-cell max-w-[42ch]">
                {b.summary}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
